import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { privateRoutes } from "router/routes"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"

import FormElement from "components/Form/FormElement"
import ProtectedLayout from "layouts/Protected"
import * as PartnerApi from "features/Partner/api/PartnerApi"
import * as UserApi from "features/api/UserApi"

import { useAlert } from "hooks/useAlert"

import { UserIcon } from "components/Elements/Icons"
import { ErrorResponse } from "models"
import { FormError } from "models/form"
import { DEFAULT_SIZE } from "utils/icons"

const defaultFormFields = {
	email: "",
}

const defaultFormEvents = {
	ready: true,
}

export const SearchUserPage = () => {
	const [formFields, setFormFields] = React.useState(defaultFormFields)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
	const [partner, setPartner] = React.useState(null)

	const { alert } = useAlert()
	const { t } = useTranslation()

	const [, setApiError] = React.useState(null)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event?.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name

		setFormFields({
			...formFields,
			[name]: value,
		})
	}

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				setApiError(null)
				// prevent form to be submitted again
				setFormEvents({
					...formEvents,
					ready: false,
				})
				const { res: getUser, getError } = await UserApi.getUserByMail(
					formFields.email.trim()
				)
				if (getError()) throw getError()

				const { res: getPartner, getError: getErrorPartner } = await PartnerApi.getPartner(
					getUser.gender_id
				)
				if (getErrorPartner()) throw getErrorPartner()
				if (getPartner) setPartner(getPartner)
			} catch (error) {
				console.error(error)
				const erroMsg = (error as ErrorResponse)?.message?.toString()
				if (erroMsg) alert("error", erroMsg)
			} finally {
				setFormEvents((s) => ({
					...s,
					ready: true,
				}))
			}
		}
	}

	return (
		<ProtectedLayout className="py-5">
			<div className="l-container">
				<div
					className={clsx("c-section-title mb-7", {
						"px-6": isMobileOnly,
						"px-8": !isMobileOnly,
					})}
				>
					<div className="c-section-title__content">
						<UserIcon size={DEFAULT_SIZE} />
						<h1 className="c-section-title__title c-h2 text-primary ml-4">
							{t("menu.search_user")}
						</h1>
					</div>
				</div>

				<div className="px-8">
					<div className="c-card p-5 lg:px-12 lg:pt-7">
						<div className="l-form-width l-form-width--l">
							<form
								data-cy="form-search-user"
								className="flex flex-row items-start mb-7"
							>
								{/* Bike Code */}
								<FormElement
									value={formFields.email}
									name="email"
									type="email"
									label={t("user.label")}
									className="mr-5 md:w-3/4"
									required={true}
									showErrors={showLocalErrors}
									onValidate={handleValidation}
									onChange={handleInputChange}
								/>

								<button
									type="submit"
									className="c-btn c-btn--primary px-12 mt-5"
									onClick={handleSubmit}
								>
									{t("global.search")}
								</button>
							</form>
						</div>
					</div>

					{partner && (
						<>
							<h3 className="pb-2 mt-7">Résultat de la recherche : </h3>
							<div className="c-card l-col-6 p-5 lg:px-12 lg:py-7">
								<h3>{partner.entity_data.raison_sociale}</h3>

								<p className="pb-5 text-xs">
									INFORMATION <br />
									<strong>Identifiant de la structure</strong> :{" "}
									{partner.entity_id}
									<br />
									<strong>Gérant</strong> :{" "}
									{partner.entity_data.prenom_representant_legal}{" "}
									{partner.entity_data.nom_representant_legal}
								</p>

								{partner.gender !== 9 &&
									partner.gender !== 10 &&
									partner.gender !== 11 &&
									partner.gender !== 12 &&
									partner.gender !== 13 && (
										<NavLink
											to={
												privateRoutes.partner.basePath +
												"/" +
												partner.entity_id
											}
											className="c-btn c-btn--default"
											style={{ maxWidth: "280px" }}
										>
											{t("user.see_partner")}
										</NavLink>
									)}
							</div>
						</>
					)}
				</div>
			</div>
		</ProtectedLayout>
	)
}
